import { PRODUCT_LABEL, PRODUCT_ORDER, PRODUCT_SLUG, TABLE_HEAD, UNASSIGNED_CODE, countryName, fmt, paymentSplitText, tableProducts } from "../lib/constants";
import { ChevronRight, MousePointerClick } from "lucide-react";
import { HINT } from "./HowWeCount";
import { shortName, initials } from "./OwnerBreakdown";

const TOP_N = 10;

// merges per-owner counts of several rows and returns the leading owner
const topOwnerOf = (rows) => {
  const counts = {};
  for (const r of rows) for (const o of r.owners || []) counts[o.name] = (counts[o.name] || 0) + o.count;
  const best = Object.entries(counts).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0];
  return best ? { name: best[0], count: best[1] } : null;
};

const sumRows = (rows, cols, pendingOf) => ({
  total: rows.reduce((s, r) => s + r.total, 0),
  legacy: rows.reduce((s, r) => s + (r.legacy || 0), 0),
  products_sold: rows.reduce((s, r) => s + (r.products_sold || 0), 0),
  upsell_products: rows.reduce((s, r) => s + (r.upsell_products || 0), 0),
  partner: rows.reduce((s, r) => s + (r.partner || 0), 0),
  pending: rows.reduce((s, r) => s + pendingOf(r.code), 0),
  payments: mergePayments(rows.map((r) => r.payments)),
  products: Object.fromEntries(cols.map((p) => [p, rows.reduce((s, r) => s + (r.products?.[p] || 0), 0)])),
  topOwner: topOwnerOf(rows),
});

// {providers:{Adyen:3}, channels:{POS:3}} of several rows added together (null when none has Payments)
const mergePayments = (list) => {
  const out = { providers: {}, channels: {} };
  let any = false;
  for (const p of list) {
    if (!p) continue;
    any = true;
    for (const k of ["providers", "channels"]) for (const [name, n] of Object.entries(p[k] || {})) out[k][name] = (out[k][name] || 0) + n;
  }
  return any ? out : null;
};

// Legacy column only when the view contains legacy venues (history mode) – the since view is always 0 there.
// Pending column (won since the cutoff, not counted yet – waiting for a CRM fix) only while there is something pending.
// compact (phones): owner shown as initials, so that column is narrow
const template = (n, withLegacy, withOwners, withPending, compact) =>
  compact
    ? `minmax(0,3fr) minmax(0,1.1fr) ${withPending ? "minmax(0,0.9fr) " : ""}${withOwners ? "minmax(0,0.8fr) " : ""}${withLegacy ? "minmax(0,0.9fr) " : ""}minmax(0,1.3fr) repeat(${n}, minmax(0,0.95fr))`
    : withPending
      ? `minmax(0,3fr) minmax(0,1.1fr) minmax(0,0.95fr) ${withOwners ? "minmax(0,1.4fr) " : ""}${withLegacy ? "minmax(0,0.9fr) " : ""}minmax(0,1.2fr) repeat(${n}, minmax(0,0.8fr))`
      : `minmax(0,2.85fr) minmax(0,1.15fr) ${withOwners ? "minmax(0,1.5fr) " : ""}${withLegacy ? "minmax(0,0.9fr) " : ""}minmax(0,1.3fr) repeat(${n}, minmax(0,0.85fr))`;

// Tailwind needs the literal class names: base min width + 56px when the Pending column is shown
const MIN_W = { 560: "min-w-[560px]", 616: "min-w-[616px]", 640: "min-w-[640px]", 680: "min-w-[680px]", 696: "min-w-[696px]", 736: "min-w-[736px]", 760: "min-w-[760px]", 816: "min-w-[816px]" };

const Cell = ({ children, className = "" }) => <span className={`num text-right pr-1 h-full flex items-center justify-end ${className}`}>{children}</span>;

const PendingCell = ({ testId, count, label, onClick }) => {
  const title = count ? `${fmt(count)} ${count === 1 ? "deal" : "deals"} won since the cutoff not counted yet in ${label} – waiting for a CRM fix${onClick ? " – click for the list" : ""}` : undefined;
  const pill = "num rounded-full bg-amber-100 text-amber-800 font-bold px-1.5 tv:px-[7px] leading-5 tv:leading-[24px] text-[11px] tv:text-[14px]";
  return (
    <span className="h-full flex items-center justify-end pr-1 min-w-0" data-testid={`${testId}-pending`} data-count={count || 0}>
      {!count ? (
        <span className="text-slate-300 num">·</span>
      ) : onClick ? (
        <button type="button" onClick={(e) => { e.stopPropagation(); onClick(); }} onKeyDown={(e) => e.stopPropagation()} title={title} aria-label={title} className={`${pill} hover:bg-amber-200 transition-colors`} data-testid={`${testId}-pending-button`}>+{fmt(count)}</button>
      ) : (
        <span className={pill} title={title}>+{fmt(count)}</span>
      )}
    </span>
  );
};

const Row = ({ testId, code, label, total, legacy, productsSold, upsell, products, payments, partner, pending, onPending, topOwner, cols, withLegacy, withOwners, withPending, compact, activeView, className = "", muted = false, bold = false, onClick }) => (
  <div
    data-testid={testId}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onClick={onClick}
    onKeyDown={onClick ? (e) => (e.key === "Enter" || e.key === " ") && onClick() : undefined}
    title={onClick ? `Click to see ${label} venues by day` : undefined}
    className={`group grid items-center text-sm tv:text-[17px] border-t border-panel-border py-1.5 tv:py-0 tv:min-h-0 ${onClick ? "cursor-pointer hover:bg-brand/[0.04] transition-colors" : ""} ${className}`}
    style={{ gridTemplateColumns: template(cols.length, withLegacy, withOwners, withPending, compact) }}
  >
    <div className="flex items-center gap-2 tv:gap-[7px] min-w-0 pl-2 tv:pl-[11px]">
      {code ? <span className="num text-[10px] tv:text-[12px] font-bold text-slate-400 w-5 tv:w-[26px] shrink-0">{code}</span> : null}
      <span className={`truncate ${bold ? "font-bold text-ink" : muted ? "text-slate-500" : "font-medium text-slate-800"}`} title={label}>{label}</span>
      {partner ? <span className="num text-[10px] tv:text-[12px] font-bold text-amber-700 bg-amber-100 rounded-full px-1.5 shrink-0" title={`${partner} via partners`} data-testid={`${testId}-partner`}>P{partner}</span> : null}
      {onClick && <ChevronRight className="w-3 h-3 tv:w-[14px] tv:h-[14px] ml-auto mr-0.5 shrink-0 text-slate-300 group-hover:text-brand group-hover:translate-x-0.5 transition-all" data-testid={`${testId}-open`} aria-hidden="true" />}
    </div>
    <Cell className={`pr-2 tv:pr-[11px] font-bold ${bold ? "text-brand" : total ? "text-ink" : "text-slate-300"} bg-brand/[0.06]`}>{total || bold ? fmt(total) : "·"}</Cell>
    {withPending && <PendingCell testId={testId} count={pending} label={label} onClick={onPending} />}
    {withOwners && (
      <span
        className={`h-full flex items-center justify-end min-w-0 pr-1 text-xs tv:text-[14px] ${bold ? "font-semibold text-ink" : muted ? "text-slate-400" : "text-slate-600"}`}
        title={topOwner ? `Top deal owner: ${topOwner.name} · ${topOwner.count} ${topOwner.count === 1 ? "venue" : "venues"}` : undefined}
        data-testid={`${testId}-top-owner`}
      >
        {!topOwner ? (
          <span className="text-slate-300">·</span>
        ) : compact ? (
          <span className={`w-6 h-6 rounded-full text-[10px] font-bold flex items-center justify-center ${bold ? "bg-ink text-page" : "bg-ink/10 text-ink"}`}>{initials(topOwner.name)}</span>
        ) : (
          <span className="truncate">{shortName(topOwner.name)}</span>
        )}
      </span>
    )}
    {withLegacy && <Cell className={bold ? "font-bold text-ink" : legacy ? "text-slate-600" : "text-slate-300"}>{legacy ? fmt(legacy) : "·"}</Cell>}
    <Cell className={`border-r border-panel-border ${bold ? "font-bold text-ink" : productsSold ? "text-slate-700 font-semibold" : "text-slate-300"}`}>
      {productsSold ? fmt(productsSold) : "·"}
      {upsell ? <sup className="num ml-0.5 text-[9px] tv:text-[11px] font-bold text-sky-700" data-testid={`${testId}-upsell`} title={`${fmt(upsell)} of ${fmt(productsSold)} products are upsells – sold since the cutoff to venues that were already clients (not counted as new venues)`}>↑{fmt(upsell)}</sup> : null}
    </Cell>
    {cols.map((p) => (
      <Cell key={p} className={bold ? "font-bold text-ink" : activeView === p && products?.[p] ? "text-brand font-bold" : products?.[p] ? "text-slate-700" : "text-slate-300"}>
        {p === "Payments" && products?.[p] && payments ? (
          <span data-testid={`${testId}-payments`} title={`${fmt(products[p])} Payments in ${label} · ${paymentSplitText(payments, { full: true })} – click the Pay header for the split on the map`} className="cursor-help underline decoration-dotted decoration-slate-300 underline-offset-2">{fmt(products[p])}</span>
        ) : products?.[p] ? fmt(products[p]) : "·"}
      </Cell>
    ))}
  </div>
);

const emptyRow = (code) => ({ code, total: 0, legacy: 0, won: 0, products_sold: 0, upsell_products: 0, partner: 0, products: {}, owners: [] });

export const TopCountriesTable = ({ countries, products, activeView, onPinProduct, highlights, historyMode, onCountryClick, compact = false, showOwners = false, maxRows = TOP_N, pending = null, onPendingClick }) => {
  // phones: only products that were actually sold, so the table needs less horizontal scrolling
  const cols = compact ? PRODUCT_ORDER.filter((p) => (products?.[p] || 0) > 0) : tableProducts(products);
  const withLegacy = historyMode === "all";
  const withOwners = !!showOwners;
  const pendingOf = (code) => pending?.[code] || 0;
  const withPending = Object.values(pending || {}).some((n) => n > 0);
  // countries with pending deals but no venues yet get a zero row, so their "+n" is visible too
  const known = new Set((countries || []).map((c) => c.code));
  const all = [...(countries || []), ...Object.keys(pending || {}).filter((code) => pendingOf(code) > 0 && !known.has(code)).map(emptyRow)];
  const unassigned = all.find((c) => c.code === UNASSIGNED_CODE);
  const named = all.filter((c) => c.code !== UNASSIGNED_CODE);
  const top = named.slice(0, maxRows);
  const rest = named.slice(maxRows);
  const other = rest.length ? sumRows(rest, cols, pendingOf) : null;
  const grand = sumRows(all, cols, pendingOf);
  const rowCount = top.length + (other ? 1 : 0) + (unassigned ? 1 : 0) + 1;
  const rowProps = (c, pendingCode) => ({
    total: c.total, legacy: c.legacy, productsSold: c.products_sold, upsell: c.upsell_products || 0, products: c.products, payments: c.payments || null, partner: c.partner, topOwner: c.topOwner ?? topOwnerOf([c]),
    pending: c.pending ?? pendingOf(c.code), onPending: onPendingClick ? () => onPendingClick(pendingCode === undefined ? c.code : pendingCode) : undefined,
    cols, withLegacy, withOwners, withPending, compact, activeView,
  });
  const head = "text-right pr-1 truncate";
  const minW = (compact ? (withOwners ? 640 : 560) : withOwners ? 760 : 680) + (withPending ? 56 : 0);

  return (
    <section className="panel flex-1 flex flex-col min-h-0 overflow-hidden fade-up" data-testid="top-countries-table">
      <div className="flex-1 min-h-0 overflow-auto thin-scroll px-2 py-2 tv:px-[13px] tv:py-[9px]">
        {/* TV: the rows share the panel height so the whole table (incl. Total) is always visible without scrolling */}
        <div
          className={`grid tv:h-full tv:min-w-0 tv:[grid-template-rows:auto_repeat(var(--rows),minmax(0,1fr))] ${MIN_W[minW]}`}
          style={{ gridTemplateColumns: "minmax(0, 1fr)", "--rows": rowCount }}
        >
          <div className={`grid items-center text-[10px] uppercase font-bold text-slate-500 pb-1 tv:pb-[4px] ${withPending ? "tv:text-[10px] tracking-tight" : "tv:text-[11px] tracking-normal"}`} style={{ gridTemplateColumns: template(cols.length, withLegacy, withOwners, withPending, compact) }}>
            <button type="button" data-testid="table-header-all" onClick={() => onPinProduct("all")} className={`text-left pl-2 tv:pl-[11px] hover:text-ink transition-colors ${activeView === "all" ? "text-brand" : ""}`}>
              Country
            </button>
            <span className={`${head} pr-2 tv:pr-[11px] text-brand`} title={HINT.total}>Venues</span>
            {withPending && <span className="text-right pr-1 whitespace-nowrap text-amber-700" title={HINT.notCounted} data-testid="table-header-pending">Pending</span>}
            {withOwners && <span className={head} title="Deal owner with the most venues in this country (click the row for the full split)">{compact ? "Owner" : "Top owner"}</span>}
            {withLegacy && <span className={head} title={HINT.legacy}>Legacy</span>}
            <span className={`${head} border-r border-panel-border`} title={HINT.productsSold}>Products</span>
            {cols.map((p) => (
              <button
                key={p}
                type="button"
                data-testid={`table-header-${PRODUCT_SLUG(p)}`}
                onClick={() => onPinProduct(p)}
                className={`text-right pr-0.5 truncate hover:text-ink transition-colors ${activeView === p ? "text-brand" : products?.[p] ? "text-slate-600" : "text-slate-400"}`}
                title={p === "Payments" ? `${PRODUCT_LABEL[p]} – venues that take card payments through FORA (once per venue). Click to pin it on the map – the map then shows the provider split (A = Adyen, V = Viva …) under each number.` : `${PRODUCT_LABEL[p]} – venues that bought this product (once per venue). Click to pin it on the map.`}
              >
                {TABLE_HEAD[p]}
              </button>
            ))}
          </div>
          {top.map((c) => (
            <Row key={c.code} testId={`country-row-${c.code}`} code={c.code} label={countryName(c.code)} {...rowProps(c)} className={highlights?.has(c.code) ? "row-flash" : ""} onClick={onCountryClick && (c.total > 0 || c.products_sold > 0) ? () => onCountryClick(c.code) : undefined} />
          ))}
          {other && <Row testId="country-row-other" code="" label={`Other (${rest.length})`} {...rowProps(other, null)} muted />}
          {unassigned && (
            <Row testId={`country-row-${UNASSIGNED_CODE}`} code="" label="No country" {...rowProps(unassigned)} muted className={highlights?.has(UNASSIGNED_CODE) ? "row-flash" : ""} onClick={onCountryClick && unassigned.total > 0 ? () => onCountryClick(UNASSIGNED_CODE) : undefined} />
          )}
          <Row testId="country-row-total" label="Total" {...rowProps(grand, null)} bold className="border-t-2 border-slate-300" />
        </div>
      </div>
      {onCountryClick && (
        <div className="shrink-0 flex items-center gap-1.5 tv:gap-[8px] px-3 py-1.5 tv:px-[13px] tv:py-[6px] border-t border-panel-border text-[10px] tv:text-[13px] text-slate-500" data-testid="table-click-hint">
          <MousePointerClick className="w-3 h-3 tv:w-[15px] tv:h-[15px] text-brand shrink-0" /> Click a country row – or a country on the map – for venues by day and deal owner
        </div>
      )}
    </section>
  );
};
